import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import { AuthContext } from '../../../context/AuthContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import PaymentGateway from '../../payment/PaymentGateway';
import { mapplsGeocode, mapplsAutosuggest, mapplsReverseGeocode, getCoordinatesFromELoc } from '../../../utils/geocoding';


const ParamedicalBooking = () => {
  const { id } = useParams(); // Get service ID from URL
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [formData, setFormData] = useState({
    appointment_date: '',
    appointment_time: '',
    address: '',
    latitude: '',
    longitude: '',
    special_instructions: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [addressLoading, setAddressLoading] = useState(true);

  // Payment flow states
  const [priceCalculated, setPriceCalculated] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [error, setError] = useState('');
  const [bookingId, setBookingId] = useState(null); // Store booking ID after book
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  // Fetch service details and user addresses
  useEffect(() => {
    if (!token) {
      alert('Please login to book a service');
      navigate('/login');
      return;
    }

    // Fetch service details
    api.get(`/paramedical-services/${id}/`)
      .then(res => {
        if (res.data.success) {
          setServiceData(res.data.data);
        } else {
          setError('Failed to load service details');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching service:', err);
        setError('Failed to load service details');
        setLoading(false);
      });

    // Fetch user addresses
    api.get('/addresses/', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        setAddresses(res.data.data || res.data.addresses || []);
        setAddressLoading(false);
      })
      .catch(err => {
        console.error('Error fetching addresses:', err);
        setAddresses([]);
        setAddressLoading(false);
      });
  }, [token, id, navigate]);

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    // Reset price calculation when form changes
    if (priceCalculated) {
      setPriceCalculated(false);
      setPriceData(null);
      setShowPayment(false);
      setPaymentData(null);
    }
  };

  const handleAddressSelect = async (address) => {
    setSelectedAddress(address);
    let lat = address.latitude?.toString() || '';
    let lng = address.longitude?.toString() || '';

    // If backend stored null lat/lng, geocode using city + state + pincode
    if (!lat || !lng) {
      try {
        const geocodeQuery = [address.city, address.state, address.pincode, 'India'].filter(Boolean).join(', ');
        const coords = await mapplsGeocode(geocodeQuery);
        if (coords) {
          lat = coords.lat.toString();
          lng = coords.lng.toString();
        }
      } catch {
        // coords stay empty; geocoding fallback in calculatePrice will retry
      }
    }

    setFormData(prev => ({
      ...prev,
      address: address.full_address || `${address.address_line_1}, ${address.city}, ${address.state} - ${address.pincode}`,
      latitude: lat,
      longitude: lng,
    }));
    // Reset price calculation when address changes
    setPriceCalculated(false);
    setPriceData(null);
    setShowPayment(false);
    setPaymentData(null);
  };

  const handleManualAddress = () => {
    setSelectedAddress(null);
    setFormData(prev => ({
      ...prev,
      address: '',
      latitude: '',
      longitude: '',
    }));
    setPriceCalculated(false);
    setPriceData(null);
    setShowPayment(false);
    setPaymentData(null);
  };

  const searchMapplsAddress = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    try {
      const places = await mapplsAutosuggest(query);
      setAddressSuggestions(places);
      setShowAddressSuggestions(places.length > 0);
    } catch (err) {
      console.error('Mappls search error:', err);
    }
  };

  const handleManualAddressInput = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, address: value, latitude: '', longitude: '' }));
    searchMapplsAddress(value);
    if (priceCalculated) {
      setPriceCalculated(false);
      setPriceData(null);
      setShowPayment(false);
      setPaymentData(null);
    }
  };

  const handleAddressSuggestionClick = (suggestion) => {
    const address = `${suggestion.placeName}${suggestion.placeAddress ? ', ' + suggestion.placeAddress : ''}`;
    const coords = getCoordinatesFromELoc(suggestion);
    setFormData(prev => ({
      ...prev,
      address,
      latitude: coords?.lat?.toString() || '',
      longitude: coords?.lng?.toString() || '',
    }));
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setPriceCalculated(false);
    setPriceData(null);
    setShowPayment(false);
    setPaymentData(null);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setFormData(prev => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lng.toString(),
        }));
        try {
          const formattedAddress = await mapplsReverseGeocode(lat, lng);
          if (formattedAddress) {
            setFormData(prev => ({ ...prev, address: formattedAddress }));
          }
        } catch (err) {
          console.error('Reverse geocode error:', err);
        }
        setPriceCalculated(false);
        setPriceData(null);
        setShowPayment(false);
        setPaymentData(null);
      },
      (err) => {
        console.error('Error getting location:', err);
        alert('Could not get your location. Please type your address and select from suggestions.');
      }
    );
  };

  // Step 1: Book on backend to get real pricing, then show payment
  const calculatePrice = async () => {
    if (!formData.appointment_date || !formData.appointment_time || !formData.address) {
      alert('Please fill all required fields to calculate price.');
      return;
    }

    setCalculatingPrice(true);
    setError('');

    let lat = parseFloat(formData.latitude);
    let lon = parseFloat(formData.longitude);

    // Geocode if lat/lng are missing (backend stored null)
    if (!formData.latitude || !formData.longitude || isNaN(lat) || isNaN(lon)) {
      try {
        const geocodeQuery = selectedAddress
          ? [selectedAddress.city, selectedAddress.state, selectedAddress.pincode, 'India'].filter(Boolean).join(', ')
          : formData.address;
        const coords = await mapplsGeocode(geocodeQuery);
        if (!coords) {
          alert('Could not determine coordinates for this address. Please use GPS or select from suggestions.');
          setCalculatingPrice(false);
          return;
        }
        lat = coords.lat;
        lon = coords.lng;
        setFormData(prev => ({
          ...prev,
          latitude: coords.lat.toString(),
          longitude: coords.lng.toString(),
        }));
      } catch {
        alert('Could not determine coordinates. Please use GPS or select from suggestions.');
        setCalculatingPrice(false);
        return;
      }
    }

    try {
      const payload = {
        service_id: parseInt(id),
        appointment_date: formData.appointment_date,
        appointment_time: formData.appointment_time,
        address: formData.address,
        latitude: lat,
        longitude: lon,
        special_instructions: formData.special_instructions || '',
        emergency_contact_name: formData.emergency_contact_name || '',
        emergency_contact_phone: formData.emergency_contact_phone || '',
      };

      const res = await api.post('/paramedical-services/book/', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        const bookingData = res.data.data;
        setBookingId(bookingData.id);
        setPriceData({
          service_price: bookingData.service_price,
          travel_cost: bookingData.travel_cost,
          total_price: bookingData.total_price,
          distance_km: bookingData.distance_km || '',
        });
        setPriceCalculated(true);
      } else {
        throw new Error(res.data.message || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to get pricing. Please try again.');
    } finally {
      setCalculatingPrice(false);
    }
  };

  // Step 2: Booking already created — just open payment
  const initiatePayment = async () => {
    if (!priceCalculated || !bookingId) {
      alert('Please calculate price first');
      return;
    }
    setShowPayment(true);
  };

  // Step 3: Payment success handler - payment captured, navigate to success
  const handlePaymentSuccess = (paymentData) => {
    navigate(`/order-success?order_id=PARA-${bookingId}&total=${priceData.total_price}&payment_id=${paymentData.razorpay_payment_id || 'PAID'}&service_type=paramedical`);
  };

  // Step 4: Payment error handler
  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setError(error.message || 'Payment failed. Please try again.');
    setShowPayment(false);
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 text-lg">Loading service details...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Error state
  if (error && !serviceData) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="text-center bg-white rounded-lg shadow-lg p-12 max-w-md mx-auto">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Service</h3>
            <p className="text-gray-500">{error}</p>
            <button
              onClick={() => navigate('/paramedical')}
              className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Services
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
        <Header />
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-full mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a2 2 0 012 2v1a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Book Service</h1>
                <p className="text-gray-600">{serviceData?.title}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Service Price</p>
              <p className="text-2xl font-bold text-blue-600">₹{serviceData?.offer_price || serviceData?.price}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Progress Indicator */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <h2 className="text-xl font-semibold text-white">Appointment Booking Process</h2>
            <div className="mt-4 flex items-center justify-center space-x-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-blue-600">1</span>
                </div>
                <span className="ml-2 text-sm font-medium text-white">Fill Details</span>
              </div>
              <div className="flex-1 max-w-[120px] border-t-2 border-blue-300"></div>
              <div className="flex items-center">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  priceCalculated ? 'bg-white' : 'bg-blue-400'
                }`}>
                  <span className={`text-sm font-bold ${
                    priceCalculated ? 'text-blue-600' : 'text-white'
                  }`}>2</span>
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  priceCalculated ? 'text-white' : 'text-blue-100'
                }`}>Calculate Price</span>
              </div>
              <div className="flex-1 max-w-[120px] border-t-2 border-blue-300"></div>
              <div className="flex items-center">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  showPayment ? 'bg-white' : 'bg-blue-400'
                }`}>
                  <span className={`text-sm font-bold ${
                    showPayment ? 'text-blue-600' : 'text-white'
                  }`}>3</span>
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  showPayment ? 'text-white' : 'text-blue-100'
                }`}>Payment & Book</span>
              </div>
            </div>
            <p className="mt-3 text-xs text-blue-100 text-center">
              {!priceCalculated
                ? "📍 Step 1: Fill in your appointment details and address"
                : !showPayment
                  ? "💰 Step 2: Review pricing and proceed to payment"
                  : "🔐 Step 3: Complete payment to confirm your booking"}
            </p>
          </div>

          {/* Form */}
          <div className="p-8">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <div className="space-y-8">
              
              {/* Address Selection Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select Service Address</h3>
                
                {addressLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading addresses...</p>
                  </div>
                ) : addresses.length > 0 ? (
                  <div className="space-y-3 mb-4">
                    <p className="text-sm text-gray-600 mb-3">Choose from your saved addresses:</p>
                    {addresses.map((address) => (
                      <div
                        key={address.id}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          selectedAddress?.id === address.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleAddressSelect(address)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium mr-2">
                                {address.address_type || 'Address'}
                              </span>
                              {selectedAddress?.id === address.id && (
                                <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <p className="text-gray-900 font-medium">
                              {address.full_address || `${address.address_line_1}${address.address_line_2 ? ', ' + address.address_line_2 : ''}`}
                            </p>
                            <p className="text-gray-600 text-sm">
                              {address.city}, {address.state} - {address.pincode}
                            </p>
                            {address.latitude && address.longitude && (
                              <p className="text-gray-500 text-xs mt-1">
                                Coordinates: {address.latitude}, {address.longitude}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <p className="text-gray-600">No saved addresses found</p>
                  </div>
                )}

                {/* Manual Address Option */}
                <div className="border-t pt-4 mt-4">
                  <button
                    type="button"
                    onClick={handleManualAddress}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Enter new address manually
                  </button>
                </div>
              </div>

              {/* Manual Address Fields */}
              {!selectedAddress && (
                <div className="space-y-4 border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <h4 className="font-medium text-gray-900">Enter Address</h4>

                  <div className="relative">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                      Search Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Type your address to search..."
                      value={formData.address}
                      onChange={handleManualAddressInput}
                      onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                      autoComplete="off"
                      required={!selectedAddress}
                    />
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                        {addressSuggestions.map((s, i) => (
                          <li
                            key={i}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={() => handleAddressSuggestionClick(s)}
                          >
                            <p className="text-sm font-medium text-gray-900">{s.placeName}</p>
                            {s.placeAddress && (
                              <p className="text-xs text-gray-500 mt-0.5">{s.placeAddress}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {formData.latitude && formData.longitude && (
                    <p className="text-xs text-green-600 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Location coordinates captured
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    className="bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-lg text-sm font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    Use My Current Location
                  </button>
                </div>
              )}

              {/* Date and Time Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="appointment_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="appointment_date"
                    name="appointment_date"
                    min={new Date().toISOString().split('T')[0]}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.appointment_date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="appointment_time" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time *
                  </label>
                  <input
                    type="time"
                    id="appointment_time"
                    name="appointment_time"
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.appointment_time}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-4 border-t pt-6">
                <h4 className="font-medium text-gray-900">Additional Information (Optional)</h4>

                <div>
                  <label htmlFor="special_instructions" className="block text-sm font-medium text-gray-700 mb-2">
                    Special Instructions
                  </label>
                  <textarea
                    id="special_instructions"
                    name="special_instructions"
                    rows={3}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any special requirements or medical conditions..."
                    value={formData.special_instructions}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="emergency_contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact Name
                    </label>
                    <input
                      type="text"
                      id="emergency_contact_name"
                      name="emergency_contact_name"
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Contact person name"
                      value={formData.emergency_contact_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="emergency_contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact Phone
                    </label>
                    <input
                      type="tel"
                      id="emergency_contact_phone"
                      name="emergency_contact_phone"
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Contact phone number"
                      value={formData.emergency_contact_phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Price Calculation Section */}
              {!priceCalculated ? (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-yellow-800">Calculate Service Price</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Get price based on your location and service distance
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={calculatePrice}
                      disabled={calculatingPrice}
                      className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {calculatingPrice ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Calculating...
                        </>
                      ) : (
                        'Calculate Price'
                      )}
                    </button>
                  </div>
                </div>
              ) : !showPayment ? (
                /* Price Display */
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                  <h4 className="font-medium text-green-800 mb-4">Price Breakdown</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-green-700">Service Price:</span>
                      <span className="font-medium text-green-800">₹{priceData.service_price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Travel Cost ({priceData.distance_km} KM @ ₹3/KM):</span>
                      <span className="font-medium text-green-800">₹{priceData.travel_cost}</span>
                    </div>
                    <div className="border-t border-green-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-green-800 font-semibold">Total Amount:</span>
                        <span className="font-bold text-green-800 text-lg">₹{priceData.total_price}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={initiatePayment}
                    className="mt-4 w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-medium flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Proceed to Secure Payment (₹{priceData.total_price})
                  </button>

                  <p className="text-xs text-green-600 text-center mt-2">
                    ✓ Service booked — complete payment to confirm
                  </p>
                </div>
              ) : (
                /* Payment Gateway */
                <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                  <PaymentGateway
                    amount={parseFloat(priceData.total_price)}
                    serviceType="paramedical_service"
                    serviceId={bookingId}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    customerDetails={{
                      name: user?.name || user?.first_name || '',
                      email: user?.email || '',
                      mobile: user?.mobile || user?.phone || ''
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />
      </>
  );
};

export default ParamedicalBooking;